import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Pagination } from '../../components/ui/Pagination';
import { Modal } from '../../components/ui/Modal';
import { DataCard, DataCardActions, DataCardEmpty, DataCardRow } from '../../components/ui/DataCard';
import { TextField } from '../../components/forms/TextField';
import { ImageUpload, type PendingFile } from '../../components/forms/ImageUpload';
import { cn } from '../../utils/cn';
import { bookingApi, type ServiceTypeEntity } from '../../api/BookingApi';
import { filesApi } from '../../api/FilesApi';
import { useI18n } from '../../i18n/I18nContext';

type CategoryDraft = {
  id?: string;
  type: string;
  sequence: string;
  image: string;
  imageUrl: string;
};

const EMPTY_DRAFT: CategoryDraft = {
  type: '',
  sequence: '',
  image: '',
  imageUrl: '',
};

export function ServiceCategoriesPage() {
  const { t } = useI18n();
  const [allCategories, setAllCategories] = useState<ServiceTypeEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(10);
  const [skip, setSkip] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [draft, setDraft] = useState<CategoryDraft>({ ...EMPTY_DRAFT });
  const [pendingImage, setPendingImage] = useState<PendingFile | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bookingApi.listServiceTypes();
      setAllCategories(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('categories.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return allCategories;
    const q = query.trim().toLowerCase();
    return allCategories.filter((c) => c.type.toLowerCase().includes(q));
  }, [allCategories, query]);

  const total = filtered.length;
  const page = useMemo(
    () => filtered.slice(skip, skip + limit),
    [filtered, skip, limit],
  );

  function handleSearchChange(value: string) {
    setQuery(value);
    setSkip(0);
  }

  function handlePageChange(next: { skip: number; limit: number }) {
    setSkip(next.skip);
    setLimit(next.limit);
  }

  function openCreate() {
    setError(null);
    setDraft({ ...EMPTY_DRAFT });
    setPendingImage(null);
    setModalOpen(true);
  }

  function openEdit(cat: ServiceTypeEntity) {
    setError(null);
    setPendingImage(null);
    setDraft({
      id: cat.serviceTypeId,
      type: cat.type,
      sequence: cat.sequence != null ? String(cat.sequence) : '',
      image: cat.image ?? '',
      imageUrl: cat.imageFile?.s3Url ?? '',
    });
    setModalOpen(true);
  }

  async function saveDraft() {
    setError(null);
    const typeName = draft.type.trim();
    if (!typeName) return setError(t('categories.validation.nameRequired'));

    const sequence = draft.sequence.trim() ? Number(draft.sequence) : undefined;
    if (draft.sequence.trim() && (isNaN(sequence!) || sequence! < 0))
      return setError(t('categories.validation.positionInvalid'));

    setSaving(true);
    try {
      let imageId: string | undefined = draft.image || undefined;
      if (pendingImage) {
        const { data: fileObj } = await filesApi.upload(pendingImage.name, pendingImage.base64);
        imageId = fileObj.fileId;
      }

      const payload: Partial<ServiceTypeEntity> & { image?: string } = {
        type: typeName,
        sequence,
        image: imageId,
      };

      if (draft.id) {
        await bookingApi.updateServiceType(draft.id, payload);
      } else {
        await bookingApi.createServiceType(payload);
      }
      setModalOpen(false);
      showToast(draft.id ? t('categories.categoryUpdated') : t('categories.categoryCreated'));
      await loadCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('categories.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function removeCategory(cat: ServiceTypeEntity) {
    const ok = window.confirm(t('categories.confirmDelete', { name: cat.type }));
    if (!ok) return;
    try {
      await bookingApi.deleteServiceType(cat.serviceTypeId);
      showToast(t('categories.categoryDeleted'));
      await loadCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('categories.deleteFailed'));
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t('categories.title')}</div>
          <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {t('categories.subtitle')}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 [&>button]:flex-1 sm:[&>button]:flex-none">
          <Button variant="secondary" onClick={loadCategories} disabled={loading}>
            {loading ? t('common.loading') : t('common.refresh')}
          </Button>
          <Button onClick={openCreate}>{t('categories.addCategory')}</Button>
        </div>
      </div>

      {toast ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
          {toast}
        </div>
      ) : null}

      {error && !modalOpen ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
          {error}
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="w-full sm:max-w-sm">
            <TextField
              label={t('common.search')}
              placeholder={t('categories.searchPlaceholder')}
              value={query}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {t('common.total')} <span className="font-semibold text-slate-700 dark:text-slate-300">{total}</span>
          </div>
        </div>

        {/* Mobile card list */}
        <div className="mt-4 space-y-3 md:hidden">
          {loading && allCategories.length === 0 ? (
            <DataCardEmpty>{t('categories.loadingCategories')}</DataCardEmpty>
          ) : page.length === 0 ? (
            <DataCardEmpty>{t('common.noResults')}</DataCardEmpty>
          ) : (
            page.map((cat) => (
              <DataCard key={cat.serviceTypeId}>
                <div className="flex items-center gap-3">
                  {cat.imageFile?.s3Url ? (
                    <img
                      src={cat.imageFile.s3Url}
                      alt={cat.type}
                      className="h-10 w-10 shrink-0 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-red-100 text-sm font-bold text-red-600 dark:bg-red-950/40 dark:text-red-300">
                      {cat.type.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="min-w-0 flex-1 truncate font-medium text-slate-900 dark:text-slate-100">
                    {cat.type}
                  </span>
                </div>
                <div className="mt-2 divide-y divide-slate-100 dark:divide-slate-800">
                  <DataCardRow label={t('categories.table.position')}>{cat.sequence ?? '—'}</DataCardRow>
                  <DataCardRow label={t('categories.table.services')}>
                    {cat.services?.length ?? 0}
                  </DataCardRow>
                </div>
                <DataCardActions>
                  <Button variant="secondary" size="sm" onClick={() => openEdit(cat)}>
                    {t('common.edit')}
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => removeCategory(cat)}>
                    {t('common.delete')}
                  </Button>
                </DataCardActions>
              </DataCard>
            ))
          )}
        </div>

        <div className="mt-4 hidden overflow-x-auto md:block">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-slate-500 dark:text-slate-400">
              <tr>
                <th className="py-2 pr-4 font-semibold">{t('categories.table.name')}</th>
                <th className="py-2 pr-4 font-semibold">{t('categories.table.position')}</th>
                <th className="py-2 pr-4 font-semibold">{t('categories.table.services')}</th>
                <th className="py-2 pr-0 font-semibold text-right">{t('categories.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading && allCategories.length === 0 ? (
                <tr>
                  <td className="py-8 text-center text-slate-500 dark:text-slate-400" colSpan={4}>
                    {t('categories.loadingCategories')}
                  </td>
                </tr>
              ) : page.length === 0 ? (
                <tr>
                  <td className="py-8 text-center text-slate-500 dark:text-slate-400" colSpan={4}>
                    {t('common.noResults')}
                  </td>
                </tr>
              ) : (
                page.map((cat) => (
                  <tr key={cat.serviceTypeId} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-3">
                        {cat.imageFile?.s3Url ? (
                          <img
                            src={cat.imageFile.s3Url}
                            alt={cat.type}
                            className="h-8 w-8 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="grid h-8 w-8 place-items-center rounded-lg bg-red-100 text-xs font-bold text-red-600 dark:bg-red-950/40 dark:text-red-300">
                            {cat.type.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className="font-medium text-slate-900 dark:text-slate-100">{cat.type}</span>
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                          'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
                        )}
                      >
                        {cat.sequence ?? '—'}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300">
                      {cat.services?.length ?? 0}
                    </td>
                    <td className="py-3 pr-0">
                      <div className="flex justify-end gap-2">
                        <Button variant="secondary" size="sm" onClick={() => openEdit(cat)}>
                          {t('common.edit')}
                        </Button>
                        <Button variant="danger" size="sm" onClick={() => removeCategory(cat)}>
                          {t('common.delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4">
          <Pagination
            total={total}
            limit={limit}
            skip={skip}
            onChange={handlePageChange}
          />
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={draft.id ? t('categories.editCategory') : t('categories.addCategory')}
        onClose={() => (saving ? null : setModalOpen(false))}
      >
        <div className="space-y-4">
          {error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
              {error}
            </div>
          ) : null}

          <ImageUpload
            label={t('categories.fields.image')}
            currentUrl={draft.imageUrl}
            pendingFile={pendingImage}
            onSelect={setPendingImage}
            onClear={() => setDraft((d) => ({ ...d, image: '', imageUrl: '' }))}
          />

          <TextField
            label={t('categories.fields.name')}
            value={draft.type}
            onChange={(e) => setDraft((d) => ({ ...d, type: e.target.value }))}
            placeholder={t('categories.fields.namePlaceholder')}
          />

          <TextField
            label={t('categories.fields.position')}
            type="number"
            value={draft.sequence}
            onChange={(e) => setDraft((d) => ({ ...d, sequence: e.target.value }))}
            placeholder={t('categories.fields.positionPlaceholder')}
            hint={t('categories.fields.positionHint')}
          />

          <div className="flex justify-end gap-2 pt-2 [&>button]:flex-1 sm:[&>button]:flex-none">
            <Button
              variant="secondary"
              onClick={() => setModalOpen(false)}
              disabled={saving}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={saveDraft} disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
